const POLL_INTERVAL_MS = 1000;
const CONNECT_TIMEOUT_MS = 30000;
const REQUEST_RETRY_MS = 8000;
const APP_AUTH_TOKEN_KEY = 'auth_token';
const CONNECT_GUEST_TOKEN_KEY = 'connect_guest_token';

const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '/api/v1';

const getIceServers = () => {
  const servers = [{ urls: 'stun:stun.l.google.com:19302' }];

  if (process.env.REACT_APP_TURN_URL) {
    servers.push({
      urls: process.env.REACT_APP_TURN_URL,
      username: process.env.REACT_APP_TURN_USERNAME || '',
      credential: process.env.REACT_APP_TURN_CREDENTIAL || ''
    });
  }

  return servers;
};

const randomSessionId = () => {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const createSignalClient = ({ serialNumber, customerId, role }) => {
  const authHeaders = () => {
    const token = localStorage.getItem(APP_AUTH_TOKEN_KEY) || localStorage.getItem(CONNECT_GUEST_TOKEN_KEY);
    if (!token) {
      throw new Error('Sign in or enter an organization code first.');
    }

    return { Authorization: `Bearer ${token}` };
  };

  const send = async (type, sessionId, payload = '') => {
    const response = await fetch(`${getApiBaseUrl()}/stream/signal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        serial_number: serialNumber,
        customer_id: customerId || 0,
        role,
        type,
        session_id: sessionId,
        payload: typeof payload === 'string' ? payload : JSON.stringify(payload)
      })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Signal failed (${response.status})`);
    }
  };

  const fetchSignals = async (afterId) => {
    const query = new URLSearchParams({
      serialNumber,
      role,
      afterId: String(afterId),
      customerId: String(customerId || 0)
    });
    const response = await fetch(`${getApiBaseUrl()}/stream/signal?${query}`, { headers: authHeaders() });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || `Signal poll failed (${response.status})`);
    }

    return Array.isArray(data.signals) ? data.signals : [];
  };

  return { send, fetchSignals };
};

// Poll loop that handles signals strictly in order, so offer/answer/candidate arrive sequentially.
const startSignalLoop = (fetchSignals, handleSignal, onError) => {
  let stopped = false;
  let lastId = 0;
  let timer = null;

  const tick = async () => {
    if (stopped) {
      return;
    }

    try {
      const signals = await fetchSignals(lastId);
      for (const signal of signals) {
        if (stopped) {
          return;
        }
        lastId = Math.max(lastId, signal.id);
        await handleSignal(signal);
      }
    } catch (error) {
      onError?.(error);
    }

    if (!stopped) {
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    }
  };

  tick();

  return () => {
    stopped = true;
    clearTimeout(timer);
  };
};

const parsePayload = (payload) => {
  try {
    return JSON.parse(payload);
  } catch (error) {
    return null;
  }
};

// Redraws the camera <img> onto a canvas every frame so it can be sent as a 30 fps video track.
export const createCanvasStream = (imageElement, fps = 30) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  let frameRequest = null;
  let active = true;

  const draw = () => {
    if (!active) {
      return;
    }

    if (imageElement.complete && imageElement.naturalWidth) {
      if (canvas.width !== imageElement.naturalWidth || canvas.height !== imageElement.naturalHeight) {
        canvas.width = imageElement.naturalWidth;
        canvas.height = imageElement.naturalHeight;
      }
      context.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
    }

    frameRequest = requestAnimationFrame(draw);
  };

  canvas.width = 640;
  canvas.height = 480;
  draw();

  const stream = canvas.captureStream(fps);

  return {
    stream,
    stop: () => {
      active = false;
      cancelAnimationFrame(frameRequest);
      stream.getTracks().forEach((track) => track.stop());
    }
  };
};

export const createRelaySender = ({ serialNumber, customerId, stream, onStatus, onViewerCount }) => {
  const signals = createSignalClient({ serialNumber, customerId, role: 'sender' });
  const peers = new Map();

  const reportViewers = () => {
    const connected = Array.from(peers.values()).filter((peer) => peer.connection.connectionState === 'connected').length;
    onViewerCount?.(connected);
  };

  const closePeer = (sessionId) => {
    const peer = peers.get(sessionId);
    if (!peer) {
      return;
    }

    clearTimeout(peer.timeout);
    peer.connection.close();
    peers.delete(sessionId);
    reportViewers();
  };

  const openPeer = async (sessionId) => {
    if (peers.has(sessionId)) {
      return;
    }

    const connection = new RTCPeerConnection({ iceServers: getIceServers() });
    const peer = { connection, pendingCandidates: [], timeout: null };
    peers.set(sessionId, peer);

    stream.getTracks().forEach((track) => connection.addTrack(track, stream));

    connection.onicecandidate = (event) => {
      if (event.candidate) {
        signals.send('candidate', sessionId, event.candidate.toJSON()).catch(() => {});
      }
    };

    connection.onconnectionstatechange = () => {
      reportViewers();
      if (['failed', 'closed', 'disconnected'].includes(connection.connectionState)) {
        closePeer(sessionId);
      }
    };

    peer.timeout = setTimeout(() => {
      if (connection.connectionState !== 'connected') {
        closePeer(sessionId);
      }
    }, CONNECT_TIMEOUT_MS);

    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    await signals.send('offer', sessionId, connection.localDescription.toJSON());
    onStatus?.('A viewer is connecting...');
  };

  const handleSignal = async (signal) => {
    if (signal.type === 'request') {
      // Skip requests left over from viewers that already gave up.
      if (signal.age_seconds <= 15) {
        await openPeer(signal.session_id);
      }
      return;
    }

    const peer = peers.get(signal.session_id);
    if (!peer) {
      return;
    }

    if (signal.type === 'bye') {
      closePeer(signal.session_id);
      return;
    }

    const data = parsePayload(signal.payload);
    if (!data) {
      return;
    }

    if (signal.type === 'answer') {
      await peer.connection.setRemoteDescription(data);
      for (const candidate of peer.pendingCandidates) {
        await peer.connection.addIceCandidate(candidate);
      }
      peer.pendingCandidates = [];
    } else if (signal.type === 'candidate') {
      if (peer.connection.remoteDescription) {
        await peer.connection.addIceCandidate(data);
      } else {
        peer.pendingCandidates.push(data);
      }
    }
  };

  onStatus?.('Sharing started. Waiting for a viewer...');
  const stopLoop = startSignalLoop(signals.fetchSignals, handleSignal, (error) => onStatus?.(error.message));

  return {
    stop: () => {
      stopLoop();
      Array.from(peers.keys()).forEach(closePeer);
    }
  };
};

export const createRelayViewer = ({ serialNumber, customerId, onStream, onStatus }) => {
  const signals = createSignalClient({ serialNumber, customerId, role: 'viewer' });
  const sessionId = randomSessionId();
  const connection = new RTCPeerConnection({ iceServers: getIceServers() });
  const pendingCandidates = [];
  let gotOffer = false;
  let retryTimer = null;
  let closed = false;

  connection.addTransceiver('video', { direction: 'recvonly' });

  connection.ontrack = (event) => {
    onStream?.(event.streams[0] || new MediaStream([event.track]));
  };

  connection.onicecandidate = (event) => {
    if (event.candidate) {
      signals.send('candidate', sessionId, event.candidate.toJSON()).catch(() => {});
    }
  };

  connection.onconnectionstatechange = () => {
    if (connection.connectionState === 'connected') {
      onStatus?.('Live');
    } else if (['failed', 'disconnected'].includes(connection.connectionState)) {
      onStatus?.('Connection lost. Press Watch live to retry.');
    }
  };

  const requestStream = () => {
    if (closed || gotOffer) {
      return;
    }

    onStatus?.('Waiting for the sharing device...');
    signals.send('request', sessionId).catch((error) => onStatus?.(error.message));
    retryTimer = setTimeout(requestStream, REQUEST_RETRY_MS);
  };

  const handleSignal = async (signal) => {
    if (signal.session_id !== sessionId) {
      return;
    }

    const data = parsePayload(signal.payload);

    if (signal.type === 'offer' && data && !gotOffer) {
      gotOffer = true;
      clearTimeout(retryTimer);
      onStatus?.('Connecting...');
      await connection.setRemoteDescription(data);
      for (const candidate of pendingCandidates) {
        await connection.addIceCandidate(candidate);
      }
      pendingCandidates.length = 0;
      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);
      await signals.send('answer', sessionId, connection.localDescription.toJSON());
    } else if (signal.type === 'candidate' && data) {
      if (connection.remoteDescription) {
        await connection.addIceCandidate(data);
      } else {
        pendingCandidates.push(data);
      }
    } else if (signal.type === 'bye') {
      onStatus?.('The sharing device stopped.');
    }
  };

  const stopLoop = startSignalLoop(signals.fetchSignals, handleSignal, (error) => onStatus?.(error.message));
  requestStream();

  return {
    stop: () => {
      closed = true;
      clearTimeout(retryTimer);
      stopLoop();
      signals.send('bye', sessionId).catch(() => {});
      connection.close();
    }
  };
};
